import LateswapCsvLineupInterface from './services/lineup-view/lateswap-csv-lineup-interface';
import EntryIdsSnippetFactory from './services/entry-ids-snippet/factory';
import ContestIdsSnippetFactory from './services/contest-ids-snippet/factory';
import ContestNamesSnippetFactory from './services/contest-names-snippet/factory';
import LineupDetailsSnippetFactory from './services/lineup-details-snippet/factory';
import LineupsSnippetFactory from './services/lineups-snippet/factory';
import LineupPlayersSnippetFactory from './services/lineup-players-snippet/factory';
import PlayersSnippetFactory from './services/players-snippet/factory';
import CsvLineup from './services/lineup-view/csv-lineup';
import CsvLineupInterface from './services/lineup-view/csv-lineup-interface';
import CsvPlayerInterpretationFactory from './services/csv-player-interpretation/factory';
import CsvPlayerInterface from './services/player-view/csv-player-interface';
import PositionsSnippetFactory from './services/positions-snippet/factory';
import CsvPlayer from './services/player-view/csv-player';
import LateswapCsvPlayer from './services/player-view/lateswap-csv-player';
import LateswapCsvPlayerInterface from './services/player-view/lateswap-csv-player-interface';
import LateswapCsvLineup from './services/lineup-view/lateswap-csv-lineup';
import ReoptimizedLineupsViewFactory from './services/reoptimized-lineups-view/factory';
import PlayersSnippetValidatorFactory from './services/snippet-validators/players-snippet-validator-factory';
import LineupsSnippetValidatorFactory from './services/snippet-validators/lineups-snippet-validator-factory';
import CsvContentParserFactory from 'services/csv/content-parser/factory';
import {removeRowsWithAtLeastOneEmptyElement, removeRowsWithAllEmptyElements} from 'services/csv/helpers';
import PlayerInterface from 'interfaces/players-data-interfaces/player-interface';

interface MapOfLocked {
    readonly players: object;
    readonly positions: object;
}

export function getMapOfLocked(
    positionsOrder: object,
    lateswapCsvLineup: LateswapCsvLineupInterface | null,
    isPlayerLocked: (player: object) => boolean,
): MapOfLocked {
    const lockedPlayersMap = {};
    const lockedPositionsMap = {};

    if (lateswapCsvLineup) {
        const lateswapPlayers = lateswapCsvLineup.getPlayers();
        Object.values(positionsOrder).map((position: object, index: number) => {
            const player: object = lateswapPlayers[index].getPlayer();

            if (isPlayerLocked(player)) {
                // @ts-ignore
                const positionId = position.id;
                // @ts-ignore
                const playerExternalId = player.externalId;

                lockedPositionsMap[positionId] = playerExternalId;
                lockedPlayersMap[playerExternalId] = positionId;
            }
        });
    }

    return {
        players: lockedPlayersMap,
        positions: lockedPositionsMap,
    };
}

export function canPlayerBeSetOnPosition(mapOfLocked: MapOfLocked, player: object, position: object): boolean {
    // @ts-ignore
    const positionId = position.id;
    // @ts-ignore
    const playerExternalId = player.externalId;

    const isPositionLocked = mapOfLocked.positions.hasOwnProperty(positionId);
    const isPlayerLocked = mapOfLocked.players.hasOwnProperty(playerExternalId);

    if (!isPositionLocked && !isPlayerLocked) {
        return true;
    }

    if (isPositionLocked) {
        return mapOfLocked.positions[positionId] === playerExternalId;
    }

    if (isPlayerLocked) {
        return mapOfLocked.players[playerExternalId] === positionId;
    }

    return true;
}

function makeLineupsSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = LineupsSnippetFactory.getSnippet(sportConfig);
    const lineupsSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAtLeastOneEmptyElement(lineupsSnippet);
}

function makePositionsSnippet(parsedContentByCells: string[][], sportConfig): string[] {
    const snippet = PositionsSnippetFactory.getSnippet(sportConfig);
    const [positions] = snippet.makeSnippet(parsedContentByCells);

    return positions;
}

function makeLineupPlayersSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = LineupPlayersSnippetFactory.getSnippet(sportConfig);
    const lineupPlayersSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAtLeastOneEmptyElement(lineupPlayersSnippet);
}

export function makePlayersSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = PlayersSnippetFactory.getSnippet(sportConfig);
    const playersSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAllEmptyElements(playersSnippet);
}

function makeEntryIdsSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = EntryIdsSnippetFactory.getSnippet(sportConfig);
    const entryIdsSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAtLeastOneEmptyElement(entryIdsSnippet);
}

function makeContestIdsSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = ContestIdsSnippetFactory.getSnippet(sportConfig);
    const entryIdsSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAtLeastOneEmptyElement(entryIdsSnippet);
}

function makeContestNamesSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = ContestNamesSnippetFactory.getSnippet(sportConfig);
    const entryIdsSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAtLeastOneEmptyElement(entryIdsSnippet);
}

function makeLineupDetailsSnippet(parsedContentByCells: string[][], sportConfig): any[] {
    const snippet = LineupDetailsSnippetFactory.getSnippet(sportConfig);
    const lineupDetailsSnippet = snippet.makeSnippet(parsedContentByCells);

    return removeRowsWithAtLeastOneEmptyElement(lineupDetailsSnippet);
}

export function parseContentByCells(content: string, sportConfig): string[][] {
    const fileParser = CsvContentParserFactory.getParser(sportConfig);

    return fileParser.parse(content);
}

export function extractCsvLineups(lineupsSnippet: string[][], sportConfig): CsvLineupInterface[] {
    const positions = makePositionsSnippet(lineupsSnippet, sportConfig);
    const entryIdsGroup = makeEntryIdsSnippet(lineupsSnippet, sportConfig);
    const lineupDetailsGroup = makeLineupDetailsSnippet(lineupsSnippet, sportConfig);
    const lineupPlayersGroup = makeLineupPlayersSnippet(lineupsSnippet, sportConfig);

    const csvPlayerInterpretation = CsvPlayerInterpretationFactory.getInterpretation(sportConfig);

    const lineups: CsvLineupInterface[] = [];
    lineupPlayersGroup.map((players: string[], lineupIndex: number) => {
        const csvPlayers: CsvPlayerInterface[] = [];
        for (let i in players) {
            const playerExternalId = csvPlayerInterpretation.interpretPlayerExternalId(players[i]);

            const csvPlayer = new CsvPlayer(playerExternalId, positions[i]);

            csvPlayers.push(csvPlayer);
        }

        const [entryId] = entryIdsGroup[lineupIndex];
        const lineupDetails = lineupDetailsGroup[lineupIndex];
        const lineupNumber = 1 + lineupIndex;

        const csvLineup = new CsvLineup(lineupNumber, entryId, lineupDetails, [...csvPlayers]);

        lineups.push(csvLineup);
    });

    return lineups;
}

function definePlayerLineupPositionNumber(
    player: PlayerInterface,
    csvPlayer: CsvPlayerInterface,
    sportConfig,
): number {
    const playerPositionNumbers = Array.isArray(player.position) ? player.position : [player.position];

    let lineupPositionNumber = sportConfig.getPositionNumberByName(csvPlayer.getLineupPosition());

    if (lineupPositionNumber === null) {
        lineupPositionNumber = playerPositionNumbers[0];
    }

    lineupPositionNumber = Number(lineupPositionNumber);

    if (!playerPositionNumbers.includes(lineupPositionNumber)) {
        throw new Error('Player position does not match lineup position.');
    }

    return lineupPositionNumber;
}

function getLateswapCsvLineupPlayers(
    csvLineup: CsvLineupInterface,
    indexedPlayers: object,
    sportConfig,
): LateswapCsvPlayerInterface[] {
    const lineupPlayers: LateswapCsvPlayerInterface[] = [];

    csvLineup.getPlayers().map((csvPlayer: CsvPlayerInterface) => {
        if (!(csvPlayer.getExternalId() in indexedPlayers)) {
            throw new Error('Player Is Absent.');
        }

        const player = cp(indexedPlayers[csvPlayer.getExternalId()]);
        // player.position = definePlayerLineupPositionNumber(player, csvPlayer, sportConfig);

        lineupPlayers.push(
            new LateswapCsvPlayer(
                csvPlayer.getExternalId(),
                csvPlayer.getLineupPosition(),
                player,
            ),
        );
    });

    return lineupPlayers;
}

export function extractLateswapCsvLineups(
    parsedContentByCells: string[][],
    sportConfig,
    indexedPlayers: object,
): LateswapCsvLineupInterface[] {
    const lineupsSnippet = makeLineupsSnippet(parsedContentByCells, sportConfig);

    assertLineupsSnippetFormatIsValid(lineupsSnippet, sportConfig);

    const csvLineups: CsvLineupInterface[] = extractCsvLineups(lineupsSnippet, sportConfig);

    const lateswapCsvLineups: LateswapCsvLineupInterface[] = [];

    csvLineups.map((csvLineup: CsvLineupInterface) => {
        let lineupPlayers: LateswapCsvPlayerInterface[];

        try {
            lineupPlayers = getLateswapCsvLineupPlayers(csvLineup, indexedPlayers, sportConfig);
        } catch (e) {
            return;
        }

        const lateswapCsvLineup = new LateswapCsvLineup(
            String(csvLineup.getLineupNumber()),
            String(csvLineup.getEntryId()),
            csvLineup.getDetails(),
            [...lineupPlayers],
        );

        lateswapCsvLineups.push(lateswapCsvLineup);
    });

    return lateswapCsvLineups;
}

export function replaceReoptimizedLineupsInCsvData(
    csvData: string[][],
    lateswapCsvLineups: LateswapCsvLineupInterface[],
    sportConfig,
): string[][] {
    const view = ReoptimizedLineupsViewFactory.getView(sportConfig);

    return view.makeView(csvData, lateswapCsvLineups);
}

export function assertPlayersSnippetFormatIsValid(playersSnippet: any[], sportConfig): void {
    const validator = PlayersSnippetValidatorFactory.getValidator(sportConfig);

    validator.assertSnippetIsValid(playersSnippet);
}

export function assertLineupsSnippetFormatIsValid(lineupsSnippet: any[], sportConfig): void {
    const validator = LineupsSnippetValidatorFactory.getValidator(sportConfig);

    validator.assertSnippetIsValid(lineupsSnippet);
}

export function getInvalidPlayerProps(player: PlayerInterface): string[] {
    const invalidProps: string[] = [];

    !player.externalId.length && invalidProps.push('externalId');
    !player.positionName.length && invalidProps.push('positionName');
    !player.team.length && invalidProps.push('team');
    !player.name.length && invalidProps.push('name');
    player.salary === null && invalidProps.push('salary');

    return invalidProps;
}
